import { CircleAlert, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ApiError } from '../lib/api.ts';
import { useT } from '../lib/i18n.tsx';
import { Button } from './ui/button.tsx';
import { Card } from './ui/card.tsx';

/** "No data for this period / filters too narrow": explains why it is empty and offers a way out. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <Inbox aria-hidden="true" className="size-8 text-muted-foreground" />
      <p className="font-medium">{title}</p>
      {description ? <p className="max-w-md text-sm text-muted-foreground">{description}</p> : null}
      {action}
    </div>
  );
}

/**
 * A widget that failed shows this and the rest of the page keeps working. The message is announced
 * to screen readers; the request id helps support find the failure.
 */
export function ErrorState({
  error,
  onRetry,
  title,
}: {
  error: ApiError | null;
  onRetry?: () => void;
  title?: string;
}) {
  const t = useT();
  return (
    <div role="alert" className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <CircleAlert aria-hidden="true" className="size-8 text-destructive" />
      <p className="font-medium">{title ?? t.errors.title}</p>
      <p className="max-w-md text-sm text-muted-foreground">{t.errors.detail}</p>
      {error?.requestId ? (
        <p className="text-xs text-muted-foreground">{t.errors.requestId(error.requestId)}</p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      ) : null}
    </div>
  );
}

/** A section card with a title, used around widgets so each can load and fail on its own. */
export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {actions}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </Card>
  );
}
